import { ErrorModal } from "@dexteel/mesf-core";
import { Alert, Box, GlobalStyles, Portal, Snackbar } from "@mui/material";
import { ExcelButton } from "@/components/buttons/excel-button";
import { PdfButton } from "@/components/buttons/pdf-button";
import { globalDefaultColDef, mesfColors } from "@setup/ag-grid/mesf-theme";
import type { ColDef, ExcelRow, GridApi } from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { DocumentsDownloadButtons } from "../AssetDocumentsGrid/components/download-buttons/documents-download-buttons.columns";
import {
	getAssemblies,
	getAssetDocuments,
	getAssetParts,
	updateAssetPartQuantity,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { openAssemblyPartsPdf } from "@/shared/assembly-pdf";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";

type ResourceType = "parts" | "drawings";

// Keep one row per key value (first-seen order). Used to collapse a drawing link whose
// DocumentName matches several catalog documents into a single detail row.
const dedupeBy = (rows: any[], key: string): any[] => {
	const seen = new Set<string>();
	const out: any[] = [];
	for (const r of rows) {
		const v = r?.[key];
		if (v == null) continue;
		const k = String(v);
		if (seen.has(k)) continue;
		seen.add(k);
		out.push(r);
	}
	return out;
};

// Assembly band row height, and how many bands are shown before the panel starts scrolling.
const ROW_H = 36;
const MAX_VISIBLE_ASSEMBLIES = 2;

// Small in-cell left padding for the first (name) column of a detail table — the gap that keeps
// the value off the cell's left border. Applied via a class (`.detail-name-cell`) + !important so
// it beats the global theme rule `.ag-cell { padding: 0 !important }`; an inline cellStyle loses
// to that !important, which is why every earlier cellStyle attempt looked glued.
const CELL_PAD = 6;

// Width of the last column of the parts table ("Qty Needed"). The action icons above the table are
// laid out over exactly that width, so they sit on top of that column.
const QTY_NEEDED_WIDTH = 120;

// Detail grids use domLayout="autoHeight"; the global default's per-row auto-height fights that
// and reserves stray empty space, so disable it here (fixed 34px rows, predictable total height).
const DETAIL_COL_DEF = {
	...globalDefaultColDef,
	autoHeight: false,
	wrapText: false,
};

// Nested grid shown when an assembly band is expanded: the assembly's own parts or drawings,
// fetched lazily by the assembly's AssetId. Drawings reuse the same PDF / ORIGINAL buttons as
// the main Documents table.
export const AssemblyResourceDetail = (params: any) => {
	const { data, resourceType, title } = params;
	// Outer indent of the whole detail table. The in-cell CELL_PAD (8px) already keeps the value
	// off the border; the Assemblies tab passes 16 so 16 + CELL_PAD lands the value under the
	// assembly name, the Parts/Documents panel uses 0 (just the in-cell pad).
	const leftPad = params.leftPad ?? 0;
	const [rows, setRows] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [notification, setNotification] = useState("");
	const [error, setError] = useState("");

	// Saved on the spot, one cell at a time. An empty cell clears the value, which means "nobody
	// stated it", and anything the SP rejects is rolled back in the grid so what is on screen
	// always matches what is stored.
	const onCellValueChanged = async (event: any) => {
		if (event.colDef.field !== "Quantity") return;

		const raw = event.newValue;
		const quantity =
			raw === "" || raw === null || raw === undefined ? null : Number(raw);

		if (quantity !== null && (!Number.isInteger(quantity) || quantity < 0)) {
			setError("The quantity must be a whole number of zero or more");
			event.node.setDataValue("Quantity", event.oldValue ?? null);
			return;
		}
		if (quantity === event.oldValue) return;

		const res = await updateAssetPartQuantity(
			data.AssetId,
			event.data.Name,
			quantity,
		);
		if (res.ok) {
			setNotification("Quantity updated successfully");
		} else {
			setError(res.message);
			event.node.setDataValue("Quantity", event.oldValue ?? null);
		}
	};

	// Same shape as the printable PDF: the parts table first, then the assembly's linked drawings
	// (document + title only) appended below it, so both deliverables carry the same content.
	const handleExportToExcel = async () => {
		if (!gridApi) return;

		const resp = await getAssetDocuments(data.AssetId, false);
		const docs = resp.ok
			? dedupeBy(resp.data?.tables?.[0]?.rows ?? [], "DocumentName")
			: [];
		const textCell = (value: any) => ({
			data: {
				type: "String" as const,
				value: value == null ? "" : String(value),
			},
		});
		const appendContent: ExcelRow[] = docs.length
			? [
					{ cells: [] },
					{ cells: [textCell(`Drawings (${docs.length})`)] },
					{ cells: [textCell("Document"), textCell("Title")] },
					...docs.map((d) => ({
						cells: [textCell(d.DocumentName), textCell(d.Title)],
					})),
				]
			: [];

		gridApi.exportDataAsExcel({
			fileName: `${data.AssetName ?? "assembly"} parts ${dxtToLocalServerTime(new Date(), "MM-dd-yyyy")}.xlsx`,
			sheetName: "Parts",
			columnKeys: ["Name", "Description", "BinName", "Qty", "Quantity"],
			appendContent,
		});
	};

	useEffect(() => {
		let alive = true;
		(async () => {
			setLoading(true);
			const resp =
				resourceType === "parts"
					? await getAssetParts(data.AssetId, false)
					: await getAssetDocuments(data.AssetId, false);
			if (!alive) return;
			const raw = resp.ok ? (resp.data?.tables?.[0]?.rows ?? []) : [];
			// Drawings: GetAssetDrawings multiplies a link when its DocumentName matches several
			// catalog documents, so collapse to one row per DocumentName (matches DrawingsCount and
			// the count badge). Parts are already grouped by the SP, so leave them untouched.
			setRows(
				resourceType === "drawings" ? dedupeBy(raw, "DocumentName") : raw,
			);
			setLoading(false);
		})();
		return () => {
			alive = false;
		};
	}, [data.AssetId, resourceType]);

	const columnDefs = useMemo<ColDef[]>(() => {
		if (resourceType === "parts") {
			return [
				{
					field: "Name",
					headerName: "Part",
					minWidth: 120,
					flex: 2,
					headerClass: "detail-name-header",
					cellClass: "detail-name-cell",
				},
				{ field: "Description", headerName: "Description", flex: 4 },
				{ field: "BinName", headerName: "Bin", minWidth: 90, flex: 1 },
				{
					// Warehouse stock. Named "Qty" like every other parts grid in the app; the units
					// this assembly needs are the "Qty Needed" column next to it.
					field: "Qty",
					headerName: "Qty",
					minWidth: 80,
					maxWidth: 100,
					type: "numericColumn",
				},
				{
					field: "Quantity",
					headerName: "Qty Needed",
					// Fixed (not just capped) so the action icons above line up with this column.
					width: QTY_NEEDED_WIDTH,
					minWidth: QTY_NEEDED_WIDTH,
					maxWidth: QTY_NEEDED_WIDTH,
					type: "numericColumn",
					editable: true,
					singleClickEdit: true,
					cellEditor: "agNumberCellEditor",
					cellEditorParams: { min: 0, precision: 0 },
					// Reads as a field rather than a value: white on the tinted band, a light outline,
					// and a pointer cursor because singleClickEdit means one click opens the editor.
					cellStyle: {
						backgroundColor: mesfColors.common.white,
						border: `1px solid ${mesfColors.grey[400]}`,
						borderRadius: "3px",
						cursor: "pointer",
					},
					// Empty, not zero, when nobody stated it.
					valueFormatter: (p: any) =>
						p.value === null || p.value === undefined ? "" : String(p.value),
				},
			];
		}
		return [
			{
				field: "DocumentName",
				headerName: "Document",
				minWidth: 130,
				flex: 2,
				headerClass: "detail-name-header",
				cellClass: "detail-name-cell",
			},
			{ field: "Title", headerName: "Title", flex: 4 },
			{ field: "Version", headerName: "Version", width: 90, maxWidth: 90 },
			{
				headerName: "Download",
				colId: "download",
				minWidth: 150,
				maxWidth: 180,
				sortable: false,
				filter: false,
				cellRenderer: DocumentsDownloadButtons,
			},
		];
	}, [resourceType]);

	return (
		<Box
			sx={{
				pt: 1,
				pb: 1,
				pr: 1,
				pl: `${leftPad}px`,
				backgroundColor: "#f5f8fc",
			}}
		>
			{/* Theme centers cells/headers with !important; a two-class selector + !important wins.
			    Header and cell share the same 6px so the name column reads as one left edge. */}
			<GlobalStyles
				styles={{
					".detail-name-header .ag-header-cell-label": {
						justifyContent: "flex-start !important",
						textAlign: "left !important",
						paddingLeft: `${CELL_PAD}px !important`,
					},
					".ag-cell.detail-name-cell": {
						justifyContent: "flex-start !important",
						paddingLeft: `${CELL_PAD}px !important`,
					},
				}}
			/>
			{/* Section title and its actions share one row. The title carries the in-cell pad so it
			    lands on the same left edge as the first column's values. */}
			{(title || resourceType === "parts") && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						minHeight: 35,
						// Air under the whole row (title and actions alike) so the actions do not sit
						// glued to the table header below.
						pb: 1.75,
						pl: `${CELL_PAD}px`,
						// No right padding: the buttons end on the grid's right edge, not 4px short of it.
						pr: 0,
					}}
				>
					<span style={{ fontWeight: 700, fontSize: 12, color: "#64748b" }}>
						{title ?? ""}
					</span>
					{resourceType === "parts" && (
						// Both icons over the last column ("Qty Needed"), one on each of its edges, so
						// the fixed column width below is what spaces them apart.
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								width: `${QTY_NEEDED_WIDTH}px`,
							}}
						>
							<ExcelButton
								iconOnly
								label="Export to Excel"
								onClick={handleExportToExcel}
								disabled={loading || rows.length === 0}
							/>
							<PdfButton
								iconOnly
								label="Printable PDF list"
								onClick={() => openAssemblyPartsPdf(data.AssetId)}
								disabled={loading || rows.length === 0}
							/>
						</Box>
					)}
				</Box>
			)}
			<AgGridReact
				domLayout="autoHeight"
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={DETAIL_COL_DEF}
				rowHeight={34}
				headerHeight={28}
				loading={loading}
				suppressContextMenu
				onGridReady={(e) => setGridApi(e.api)}
				onCellValueChanged={onCellValueChanged}
				stopEditingWhenCellsLoseFocus
			/>
			{/* Portal is required here: Snackbar is position:fixed and does NOT portal on its own,
			    and this grid lives inside an AG Grid detail row, whose parent row is positioned with
			    a CSS transform. That transform re-anchors position:fixed to the row, so without the
			    portal the toast opens inside the row and the grid's overflow hides it. */}
			<Portal>
				<Snackbar
					open={!!notification}
					autoHideDuration={2500}
					onClose={() => setNotification("")}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert severity="success" onClose={() => setNotification("")}>
						{notification}
					</Alert>
				</Snackbar>
			</Portal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};

// AG Grid cell renderer for the Assembly name column on the Assemblies tab: our own chevron
// (flush-left, no agGroupCellRenderer indent) + name. Clicking the chevron/name toggles the
// master-detail row via node.setExpanded (only this cell toggles — not the whole row). Asset
// path / counts live in their own columns.
export const AssemblyRowCell = (params: any) => {
	const [expanded, setExpanded] = useState<boolean>(!!params.node?.expanded);
	const a = params.data;
	if (!a) return null;
	const toggle = () => {
		const next = !params.node?.expanded;
		params.node?.setExpanded(next);
		setExpanded(next);
	};
	return (
		<span
			onClick={toggle}
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 8,
				cursor: "pointer",
			}}
		>
			<i
				className={expanded ? "fas fa-chevron-down" : "fas fa-chevron-right"}
				style={{ fontSize: 11, width: 16, color: "#5f6b7a" }}
			/>
			<span style={{ fontWeight: 600 }}>{a.AssetName}</span>
		</span>
	);
};

// Expanded content for an assembly on the Assemblies tab (its own tab, no single resource
// context): both the assembly's parts and its drawings, stacked.
export const AssemblyBothResourcesDetail = (params: any) => {
	// Each table renders its own title row, so the label sits level with its action buttons.
	// leftPad 18 + the in-cell 6 lands both the title and the values at 24px, which is where the
	// assembly name starts (chevron 16 + gap 8) — everything shares one left edge.
	const tableLeftPad = 24 - CELL_PAD;
	return (
		<Box
			sx={{
				pt: 1,
				pr: 1,
				pb: 1,
				pl: 0,
				backgroundColor: "#f5f8fc",
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
			}}
		>
			<AssemblyResourceDetail
				data={params.data}
				resourceType="parts"
				leftPad={tableLeftPad}
				title={`Parts (${params.data?.PartsCount ?? 0})`}
			/>
			<AssemblyResourceDetail
				data={params.data}
				resourceType="drawings"
				leftPad={tableLeftPad}
				title={`Drawings (${params.data?.DrawingsCount ?? 0})`}
			/>
		</Box>
	);
};

interface Props {
	assetId: number | null | undefined;
	resourceType: ResourceType;
	includeDescendants?: boolean;
	// bumped by the parent to force a refetch (e.g. after linking on the asset)
	refreshKey?: number;
}

// Assemblies section rendered BELOW the Parts / Documents table, inside the same grid container.
// Each assembly is a yellow band (chevron + name + resource count) that expands to its own
// parts / drawings. Kept separate from the main table so the assembly's detail columns never
// clash with the document/part columns. Built from plain rows (not AG Grid master-detail) so the
// section height always fits its content exactly, capped at MAX_VISIBLE_ASSEMBLIES then scrolls.
// Renders nothing when the asset has no assemblies.
export const AssembliesMasterDetailPanel = ({
	assetId,
	resourceType,
	includeDescendants = false,
	refreshKey = 0,
}: Props) => {
	const [assemblies, setAssemblies] = useState<any[]>([]);
	const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

	useEffect(() => {
		let alive = true;
		(async () => {
			if (!assetId) {
				setAssemblies([]);
				setExpandedIds(new Set());
				return;
			}
			const resp = await getAssemblies(assetId, includeDescendants);
			if (!alive) return;
			setAssemblies(resp.ok ? (resp.data?.tables?.[0]?.rows ?? []) : []);
			setExpandedIds(new Set());
		})();
		return () => {
			alive = false;
		};
	}, [assetId, includeDescendants, refreshKey]);

	const countField = resourceType === "parts" ? "PartsCount" : "DrawingsCount";
	const bandBg = mesfColors.resource[resourceType];

	if (!assemblies.length) return null;

	const anyExpanded = expandedIds.size > 0;
	// Collapsed: cap at MAX_VISIBLE_ASSEMBLIES bands (scroll past that). Expanded: allow more
	// height (bounded, internal scroll) so the open detail never overflows the container. The
	// Box height is content-driven, so it never leaves trailing empty space.
	const maxHeight = anyExpanded
		? "55%"
		: `${ROW_H * MAX_VISIBLE_ASSEMBLIES + 2}px`;

	const toggle = (id: number) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	return (
		<Box sx={{ flexShrink: 0, mt: "5px", maxHeight, overflowY: "auto" }}>
			{assemblies.map((a) => {
				const isOpen = expandedIds.has(a.AssetId);
				const count = a[countField];
				return (
					<div key={a.AssetId}>
						{/* Band: 30px spacer (checkbox column) + 150px centered name column, mirroring
						    the main grid so the assembly name lines up with the document/part codes. */}
						<div
							onClick={() => toggle(a.AssetId)}
							title={`Assembly · expand to see its ${
								resourceType === "parts" ? "parts" : "drawings"
							}`}
							style={{
								display: "flex",
								alignItems: "center",
								height: ROW_H,
								background: bandBg,
								borderBottom: `1px solid ${mesfColors.grey.divider}`,
								cursor: "pointer",
								fontSize: 13,
							}}
						>
							<div style={{ width: 30, flexShrink: 0 }} />
							<div
								style={{
									width: 150,
									flexShrink: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<i
									className={
										isOpen ? "fas fa-chevron-down" : "fas fa-chevron-right"
									}
									style={{ fontSize: 11, width: 16, color: "#5f6b7a" }}
								/>
								<span style={{ fontWeight: 600 }}>{a.AssetName}</span>
								{typeof count === "number" ? (
									<span
										style={{ fontWeight: 400, color: "#6b7684", marginLeft: 6 }}
									>
										({count})
									</span>
								) : null}
							</div>
							<div
								style={{
									flex: 1,
									display: "flex",
									alignItems: "center",
									paddingLeft: 8,
									color: "#94a3b8",
									fontSize: 12,
									overflow: "hidden",
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
								}}
							>
								{a.FullPath || ""}
							</div>
						</div>
						{isOpen ? (
							<AssemblyResourceDetail data={a} resourceType={resourceType} />
						) : null}
					</div>
				);
			})}
		</Box>
	);
};
